import React, { useState, useMemo, useEffect } from 'react';
import { Plus, BarChart3, List, FileText } from 'lucide-react';
import { Button } from '../../common/Button';
import { PageHeader } from '../../layouts/PageHeader';
import type { UseSubscriptionDataResult } from '../../../hooks/useSubscriptionData';
import type { Subscription, SubscriptionFilters } from '../../../types/subscriptionManagement';
import { SubscriptionFilterBar } from './SubscriptionFilterBar';
import { SubscriptionKpiStrip } from './SubscriptionKpiStrip';
import { SubscriptionTable } from './SubscriptionTable';
import { SubscriptionFormModal } from './SubscriptionFormModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { SubscriptionDetailScreen } from './SubscriptionDetail/SubscriptionDetailScreen';
import { AnalyticsSection } from './AnalyticsSection';
import { MonthlyExpenseReport } from './MonthlyExpenseReport';
import { loadFxRates } from '../../../utils/fxUtils';
import {
  availableFYsFromData,
  currentFyStart,
  invoiceInFy,
  subscriptionVisibleInFy,
} from '../../../utils/subscriptionFyUtils';
import { SubscriptionFySelect, type SubscriptionFySelectValue } from './subscription-fy-select';

// ─── Tabs config ──────────────────────────────────────────────────────────────
type TabId = 'subscriptions' | 'analysis' | 'report';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'subscriptions', label: 'Subscriptions', icon: List },
  { id: 'analysis',      label: 'Analysis',       icon: BarChart3 },
  { id: 'report',        label: 'Report',          icon: FileText },
];

// ─── Loading skeleton ─────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 animate-pulse">
    <div className="h-2 w-20 bg-slate-200 rounded mb-3" />
    <div className="h-6 w-28 bg-slate-200 rounded" />
  </div>
);
const LoadingSkeleton: React.FC = () => (
  <div className="p-6 space-y-4">
    <div className="grid grid-cols-4 gap-2">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-48 animate-pulse" />
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
export const SubscriptionManagement: React.FC<UseSubscriptionDataResult> = (props) => {
  const {
    subscriptions, invoices, vendors, paymentMethods, lookupColumns, loading, saving, error,
    createSubscription, updateSubscription, deleteSubscription,
    createInvoice, updateInvoice, deleteInvoice, uploadInvoiceFile,
    refresh,
  } = props;

  // ── View state ────────────────────────────────────────────────────────
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('subscriptions');
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Subscription | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Subscription | null>(null);
  const [employees, setEmployees] = useState<string[]>([]);
  /** New vendor strings typed in the form before first save (not in snapshot yet). */
  const [addedVendorNames, setAddedVendorNames] = useState<string[]>([]);

  useEffect(() => {
    loadFxRates()
  }, [])

  // ── Fetch employees on mount ──────────────────────────────────────────
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch('/api/employees?gradeFilter=S05');
        if (res.ok) {
          const data = await res.json();
          const names = Array.isArray(data)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ? data.map((e: any) => e.name || e.emp_name || '').filter(Boolean)
            : [];
          setEmployees(names);
        }
      } catch { /* no-op — employees list stays empty */ }
    };
    fetchEmployees();
  }, []);

  const vendorComboOptions = useMemo(() => {
    const s = new Set<string>([...vendors.map(v => v.name), ...addedVendorNames]);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [vendors, addedVendorNames]);

  const paymentMethodComboOptions = useMemo(
    () => paymentMethods.map(pm => pm.name),
    [paymentMethods],
  );

  const fyOptions = useMemo(
    () => availableFYsFromData(subscriptions, invoices),
    [subscriptions, invoices],
  );
  const [fySelection, setFySelection] = useState<SubscriptionFySelectValue>(() => currentFyStart());
  useEffect(() => {
    if (fyOptions.length === 0) return;
    if (fySelection === 'all') return;
    if (!fyOptions.includes(fySelection)) {
      setFySelection(
        fyOptions.includes(currentFyStart()) ? currentFyStart() : fyOptions[0],
      );
    }
  }, [fyOptions, fySelection]);

  const subscriptionsInFy = useMemo(
    () =>
      fySelection === 'all'
        ? [...subscriptions]
        : subscriptions.filter(s =>
            subscriptionVisibleInFy(s, invoices, fySelection),
          ),
    [subscriptions, invoices, fySelection],
  );
  const invoicesInFy = useMemo(
    () =>
      fySelection === 'all'
        ? [...invoices]
        : invoices.filter(i => invoiceInFy(i, fySelection)),
    [invoices, fySelection],
  );

  /** Subscriptions tab: all records. Analysis & Report: scoped to header FY. */
  const fyScopedView = activeTab !== 'subscriptions';
  const subscriptionsPool = fyScopedView ? subscriptionsInFy : subscriptions;
  const invoicesPool = fyScopedView ? invoicesInFy : invoices;

  // ── Filters ───────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<SubscriptionFilters>({
    search: '', department: 'all', status: 'all', billingCycle: 'all', category: 'all',
  });

  const filtered = useMemo(() => {
    let out = subscriptionsPool;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.vendor?.toLowerCase().includes(q) ||
        s.owner?.toLowerCase().includes(q),
      );
    }
    if (filters.department !== 'all') out = out.filter(s => s.department === filters.department);
    if (filters.status !== 'all')     out = out.filter(s => s.status === filters.status);
    if (filters.billingCycle !== 'all') out = out.filter(s => s.billingCycle === filters.billingCycle);
    if (filters.category !== 'all')   out = out.filter(s => s.category === filters.category);
    return out;
  }, [subscriptionsPool, filters]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleAdd = () => { setEditTarget(null); setFormOpen(true); };
  const handleEdit = (sub: Subscription) => { setEditTarget(sub); setFormOpen(true); };
  const handleDelete = (sub: Subscription) => setDeleteTarget(sub);

  const handleAddVendor = (vendor: string) => {
    const t = vendor.trim();
    if (!t) return;
    setAddedVendorNames(prev => (prev.includes(t) ? prev : [...prev, t]));
  };

  const handleFormSave = async (data: Partial<Subscription>) => {
    if (editTarget) await updateSubscription(editTarget.id, data);
    else await createSubscription(data);
    setFormOpen(false);
    setEditTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteSubscription(deleteTarget.id);
    setDeleteTarget(null);
  };

  // ── Error state ───────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <PageHeader title="Subscription Management" />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-500 text-sm">{error}</p>
          </div>
        </div>
      </>
    );
  }

  // ── Detail view ───────────────────────────────────────────────────────
  if (selectedSub) {
    const liveSub = subscriptions.find(s => s.id === selectedSub.id) || selectedSub;
    const subInvoices = invoices.filter(inv => inv.subscriptionId === liveSub.id);
    return (
      <>
        <SubscriptionDetailScreen
          subscription={liveSub}
          allSubscriptions={subscriptions}
          paymentMethodNames={paymentMethods.map(pm => pm.name)}
          lookupColumns={lookupColumns}
          refreshSnapshot={refresh}
          invoices={subInvoices}
          saving={saving}
          onBack={() => setSelectedSub(null)}
          onEdit={() => handleEdit(liveSub)}
          onCreateInvoice={createInvoice}
          onUpdateInvoice={updateInvoice}
          onDeleteInvoice={deleteInvoice}
          onUploadFile={uploadInvoiceFile}
        />

        {/* Edit modal must render here too — list-view return is unreachable in detail view */}
        <SubscriptionFormModal
          isOpen={formOpen}
          onClose={() => { setFormOpen(false); setEditTarget(null); }}
          onSave={handleFormSave}
          subscription={editTarget}
          vendors={vendorComboOptions}
          paymentMethods={paymentMethodComboOptions}
          lookupColumns={lookupColumns}
          refreshSnapshot={refresh}
          saving={saving}
          onAddVendor={handleAddVendor}
          onAddDepartment={() => {}}
          onAddCategory={() => {}}
          employees={employees}
        />
      </>
    );
  }

  const fyHeaderFilter = (
    <SubscriptionFySelect
      value={fySelection}
      options={fyOptions}
      onChange={setFySelection}
    />
  );

  const fyKpiLabels = fyScopedView && fySelection !== 'all';

  // ── List view ─────────────────────────────────────────────────────────
  const pageSubtitle = !fyScopedView
    ? 'All subscriptions · use Analysis or Report for FY-scoped views'
    : fySelection === 'all'
      ? 'All FY · list and metrics include every period'
      : 'Apr–Mar FY · list and metrics match selection';

  return (
    <>
      <PageHeader
        title="Subscription Management"
        subtitle={pageSubtitle}
        filters={fyScopedView ? fyHeaderFilter : undefined}
      />

      {loading ? <LoadingSkeleton /> : (
        <div className="p-6 space-y-4">

          {/* KPI Strip */}
          <SubscriptionKpiStrip
            subscriptions={subscriptionsPool}
            invoices={invoicesPool}
            fyScoped={fyKpiLabels}
          />

          {/* ── Tab Bar ───────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
            <div className="flex border-b border-slate-200 overflow-x-auto">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3.5 text-[12px] font-semibold whitespace-nowrap transition-all border-b-2 -mb-px flex-none ${
                      active
                        ? 'border-blue-500 text-blue-600 bg-blue-50/30'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={13} className={active ? 'text-blue-500' : 'text-slate-400'} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Tab: Subscriptions ────────────────────────────────────── */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-3">
              {/* Toolbar: filters + add button */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <SubscriptionFilterBar
                  filters={filters}
                  onChange={setFilters}
                  subscriptions={subscriptionsPool}
                />
                <Button variant="primary" size="sm" type="button" onClick={handleAdd}>
                  <Plus size={14} /> Add Subscription
                </Button>
              </div>

              {/* Row count */}
              <p className="text-xs font-semibold text-slate-400">
                {filtered.length} subscription{filtered.length !== 1 ? 's' : ''}
                {filtered.length !== subscriptionsPool.length &&
                  ` (filtered from ${subscriptionsPool.length}${fyKpiLabels ? ' in FY' : ''})`}
              </p>

              <SubscriptionTable
                subscriptions={filtered}
                invoices={invoicesPool}
                onRowClick={sub => setSelectedSub(sub)}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>
          )}

          {/* ── Tab: Analysis ─────────────────────────────────────────── */}
          {activeTab === 'analysis' && (
            subscriptionsInFy.length > 0
              ? (
                <AnalyticsSection
                  subscriptions={subscriptionsInFy}
                  invoices={invoicesInFy}
                  allInvoices={invoices}
                  fyStart={fySelection}
                />
              )
              : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm py-16 text-center">
                  <BarChart3 size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm font-medium text-slate-500">No data yet</p>
                  <p className="text-xs text-slate-400 mt-1">Add subscriptions to see analytics</p>
                </div>
              )
          )}

          {/* ── Tab: Report ───────────────────────────────────────────── */}
          {activeTab === 'report' && (
            invoicesInFy.length > 0
              ? (
                <MonthlyExpenseReport
                  subscriptions={subscriptionsInFy}
                  invoices={invoicesInFy}
                  fyStart={fySelection}
                />
              )
              : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm py-16 text-center">
                  <FileText size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm font-medium text-slate-500">No invoice data yet</p>
                  <p className="text-xs text-slate-400 mt-1">Add invoices to subscriptions to see the expense report</p>
                </div>
              )
          )}

        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────── */}
      <SubscriptionFormModal
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditTarget(null); }}
        onSave={handleFormSave}
        subscription={editTarget}
        vendors={vendorComboOptions}
        paymentMethods={paymentMethodComboOptions}
        lookupColumns={lookupColumns}
        refreshSnapshot={refresh}
        saving={saving}
        onAddVendor={handleAddVendor}
        onAddDepartment={() => {}}
        onAddCategory={() => {}}
        employees={employees}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Subscription"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        saving={saving}
      />
    </>
  );
};
